import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveProfile } from "@/hooks/useActiveProfile";
import { logDataSource } from "@/lib/dataSourceLogger";
import { subscriptionCache, CACHE_DURATIONS } from "@/lib/queryCache";

interface Subscription {
  subscription_id: string;
  product_id: string;
  platform: string;
  tier: string;
  status: string;
  current_period_end: string;
}

// Canonical Stripe catalog mirror — values MUST match
// supabase/functions/_shared/stripeConfig.ts STRIPE_PRODUCTS.
// Parity is enforced by src/test/stripe-config-parity.test.ts.
//
// `contactSalesOnly: true` removes the tier from self-serve checkout
// (Coach Studio + Founder Pilot per canonical 2026-05-29).
// `selfServe: false` modules still appear here so webhooks/check-subscription
// can resolve manually-created subs to entitlements.
export const STRIPE_PRODUCTS = {
  // ── Callback Zone ────────────────────────────────────────────────
  callback_zone_starter: {
    price_id: "price_1ThuUdPffCeWRbaqtXTHbuFI",
    product_id: "prod_UhJ68NFvSTX5ch",
    platform: "callback_zone",
    tier: "starter",
    name: "Callback Zone Starter",
    price: 29,
    interval: "month",
  },
  callback_zone_premium: {
    price_id: "price_1TaorpB0zPLoYQLklk6x7cO0",
    product_id: "prod_UZypIquIDmscCZ",
    platform: "callback_zone",
    tier: "premium",
    name: "Callback Zone Family Pro",
    price: 49,
    interval: "month",
  },
  callback_zone_starter_annual: {
    price_id: "price_1TgVitB0zPLoYQLkha3GeoxP",
    product_id: "prod_UfrRHWm52gThKy",
    platform: "callback_zone",
    tier: "starter_annual",
    name: "Callback Zone Starter (Annual)",
    price: 290,
    interval: "year",
  },
  callback_zone_premium_annual: {
    price_id: "price_1TgViuB0zPLoYQLkgNoTfMRN",
    product_id: "prod_UfrR2ED9WnU62R",
    platform: "callback_zone",
    tier: "premium_annual",
    name: "Callback Zone Family Pro (Annual)",
    price: 490,
    interval: "year",
  },

  // ── CastReady Coach (self-serve solo only) ───────────────────────
  coach_starter: {
    price_id: "price_1Tap4yB0zPLoYQLkN3OtfoQx",
    product_id: "prod_UZz3UtYH5Z0CP5",
    platform: "coach",
    tier: "coach_starter",
    name: "Coach Solo Starter",
    price: 49,
    interval: "month",
  },
  coach_starter_annual: {
    price_id: "price_1TgVirB0zPLoYQLknhmqV8yk",
    product_id: "prod_UfrRaVGm4NyRIv",
    platform: "coach",
    tier: "coach_starter_annual",
    name: "Coach Solo Starter (Annual)",
    price: 490,
    interval: "year",
  },
  coach_pro: {
    price_id: "price_1Tap4zB0zPLoYQLkk6tBKYGR",
    product_id: "prod_UZz3qI9AQKSb7A",
    platform: "coach",
    tier: "coach_pro",
    name: "Coach Solo Pro",
    price: 119,
    interval: "month",
  },
  coach_pro_annual: {
    price_id: "price_1TgVisB0zPLoYQLkNmq60vLh",
    product_id: "prod_UfrRBm2IxrSXzp",
    platform: "coach",
    tier: "coach_pro_annual",
    name: "Coach Solo Pro (Annual)",
    price: 1190,
    interval: "year",
  },
  coach_white_label_solo: {
    price_id: "price_1Tap50B0zPLoYQLk3ASW7erR",
    product_id: "prod_UZz3TzUQVotfBk",
    platform: "coach",
    tier: "coach_white_label_solo",
    name: "Coach White-Label Solo",
    price: 79,
    interval: "month",
    // SOT v3 does not enumerate a $79 white-label solo SKU. Kept for
    // in-flight subs only; hidden from self-serve UI (see modules.ts +
    // PlanStep.tsx). Remove fully when no active subscribers remain.
    contactSalesOnly: true,
  },

  // ── Coach Studio — CONTACT SALES ONLY (canonical 2026-05-29) ────
  // Listed for webhook/entitlement resolution; never offered in self-serve UI.
  coach_studio_starter: {
    price_id: "price_1TcKFYB0zPLoYQLkN6HkcXtK",
    product_id: "prod_UbXKZogHvekWKI",
    platform: "coach",
    tier: "studio_starter",
    name: "Coach Studio Starter",
    price: 349,
    interval: "month",
    contactSalesOnly: true,
  },
  coach_studio_pro: {
    price_id: "price_1TcKFbB0zPLoYQLkIptRnt7l",
    product_id: "prod_UbXKEa0MSSOKoc",
    platform: "coach",
    tier: "studio_pro",
    name: "Coach Studio Pro",
    price: 599,
    interval: "month",
    contactSalesOnly: true,
  },
  coach_studio_scale: {
    price_id: "price_1TcKFdB0zPLoYQLkUDprb1GF",
    product_id: "prod_UbXKnA1vYFj74m",
    platform: "coach",
    tier: "studio_scale",
    name: "Coach Studio Scale",
    price: 799,
    interval: "month",
    contactSalesOnly: true,
  },
  founding_studio_pilot: {
    price_id: "price_1Tb1aXB0zPLoYQLkGejAxllS",
    product_id: "prod_UaByJttvObkatN",
    platform: "coach",
    tier: "founding_studio_pilot",
    name: "Founding Studio Pilot",
    price: 199,
    interval: "month",
    contactSalesOnly: true,
  },

  // ── Castings ─────────────────────────────────────────────────────
  castings_talent_basic: {
    price_id: "price_1TcKFfB0zPLoYQLkCK8glrxQ",
    product_id: "prod_UbXK2hcG8IGTND",
    platform: "castings",
    tier: "talent_basic",
    name: "Castings Talent Basic",
    price: 9,
    interval: "month",
  },
  castings_talent_premium: {
    price_id: "price_1TaorqB0zPLoYQLkevzJ9DkJ",
    product_id: "prod_UZypRQmh3NzVll",
    platform: "castings",
    tier: "talent_premium",
    name: "Castings Talent Pro",
    price: 19,
    interval: "month",
  },
  castings_talent_annual: {
    price_id: "price_1SiHPjB0zPLoYQLkhwAmKkp5",
    product_id: "prod_TfcfvMRAbOWryC",
    platform: "castings",
    tier: "talent_premium_annual",
    name: "Castings Talent Annual",
    price: 290,
    interval: "year",
  },
  castings_team_starter: {
    price_id: "price_1TgVidB0zPLoYQLkCg9HhFl7",
    product_id: "prod_UfrRuhRaUP5AMq",
    platform: "castings",
    tier: "team_starter",
    name: "Castings Office Starter",
    price: 149,
    interval: "month",
  },
  castings_team_pro: {
    price_id: "price_1TgVieB0zPLoYQLkLXw7LmZj",
    product_id: "prod_UfrRlSSqM3QvFk",
    platform: "castings",
    tier: "team_pro",
    name: "Castings Office Pro",
    price: 399,
    interval: "month",
  },
  castings_agency_pro: {
    price_id: "price_1TgVifB0zPLoYQLkfzFmcu7h",
    product_id: "prod_UfrRZCuw53yR5Z",
    platform: "castings",
    tier: "agency_pro",
    name: "Castings Agency Pro",
    price: 899,
    interval: "month",
  },

  // ── Bundles ──────────────────────────────────────────────────────
  actor_growth_bundle: {
    price_id: "price_1TcKFiB0zPLoYQLkzy98VQvT",
    product_id: "prod_UbXKoYjPxhzGy9",
    platform: "bundle",
    tier: "actor_growth",
    name: "Actor Growth Bundle — Starter",
    price: 35,
    interval: "month",
    includes: ["castings", "callback_zone"],
  },
  actor_growth_bundle_family_pro: {
    price_id: "price_1TcKFjB0zPLoYQLkxwLMYQ8e",
    product_id: "prod_UbXKrm2biNXs0x",
    platform: "bundle",
    tier: "actor_growth_family_pro",
    name: "Actor Growth Bundle — Family Pro",
    price: 59,
    interval: "month",
    includes: ["castings", "callback_zone"],
  },

  // ── Pro (invite-only partner portal) ─────────────────────────────
  pro: {
    price_id: "custom",
    product_id: "partner_portal",
    platform: "pro",
    tier: "partner",
    name: "Partner Access",
    price: null as number | null,
    interval: "custom",
    contactSalesOnly: true,
  },

  // ── HMU / Crew / Capture / Draftline — single $49 Starter ────────
  hmu_starter: {
    price_id: "price_1TcKFkB0zPLoYQLkm85yuo6P",
    product_id: "prod_UbXKVjpWcGYPME",
    platform: "hmu",
    tier: "hmu_starter",
    name: "HMU Starter",
    price: 49,
    interval: "month",
  },
  crew_starter: {
    price_id: "price_1TcKFnB0zPLoYQLkIsIkvRl1",
    product_id: "prod_UbXKyh5Mod5wuP",
    platform: "crew",
    tier: "crew_starter",
    name: "Crew Starter",
    price: 49,
    interval: "month",
  },
  capture_starter: {
    price_id: "price_1TcKFrB0zPLoYQLklLxod0PY",
    product_id: "prod_UbXKb9z7TnkIqq",
    platform: "capture",
    tier: "capture_starter",
    name: "Capture Starter",
    price: 49,
    interval: "month",
  },
  draftline_starter: {
    price_id: "price_1TcKFpB0zPLoYQLkuoXJT1w0",
    product_id: "prod_UbXKWEyqznkcez",
    platform: "draftline",
    tier: "draftline_starter",
    name: "Draftline Starter",
    price: 49,
    interval: "month",
  },

  // ── AI Credit Packs (one-time, in-module top-up) ─────────────────
  ai_credit_pack_small: {
    price_id: "price_1TcKFtB0zPLoYQLkr3iQrarr",
    product_id: "prod_UbXKieuxWK7VqU",
    platform: "ai_credits",
    tier: "pack_small",
    name: "AI Credit Pack — Small (2,500)",
    price: 9,
    interval: "one_time",
  },
  ai_credit_pack_standard: {
    price_id: "price_1TcKFuB0zPLoYQLkjQTriRUK",
    product_id: "prod_UbXKSuRsCrb76U",
    platform: "ai_credits",
    tier: "pack_standard",
    name: "AI Credit Pack — Standard (7,500)",
    price: 19,
    interval: "one_time",
  },
  ai_credit_pack_pro: {
    price_id: "price_1TcKFvB0zPLoYQLkdrLGw3Mo",
    product_id: "prod_UbXKouzatpSp9I",
    platform: "ai_credits",
    tier: "pack_pro",
    name: "AI Credit Pack — Pro (25,000)",
    price: 49,
    interval: "one_time",
  },
} as const;

export type ProductKey = keyof typeof STRIPE_PRODUCTS;

/**
 * Manages Stripe subscriptions: check status, create checkouts, open portal,
 * and query per-platform access including bundles and child profiles.
 */
export const useSubscription = () => {
  const { user } = useAuth();
  const { effectiveUserId, isActingAsChild, parentUserId } = useActiveProfile();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [childSubscriptions, setChildSubscriptions] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fallback: Query subscriptions directly from database by email
  const fetchSubscriptionsDirectly = useCallback(async (userEmail: string): Promise<Subscription[]> => {
    try {
      // First, find the profile by email
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", userEmail)
        .maybeSingle();
      
      if (profileError) {
        console.error("[useSubscription] Profile lookup error:", profileError);
        return [];
      }
      
      if (!profile) {
        return [];
      }
      
      // Fetch subscriptions for this profile
      const { data: subs, error: subsError } = await supabase
        .from("subscriptions")
        .select("id, stripe_subscription_id, platform, tier, status, current_period_end, user_id")
        .eq("user_id", profile.id)
        .eq("status", "active");
      
      if (subsError) {
        return [];
      }
      
      // Map to Subscription interface
      return (subs || []).map(sub => ({
        subscription_id: sub.id,
        product_id: sub.stripe_subscription_id || "",
        platform: sub.platform,
        tier: sub.tier,
        status: sub.status,
        current_period_end: sub.current_period_end || ""
      }));
    } catch (err) {
      console.error("[useSubscription] Direct query failed:", err);
      return [];
    }
  }, []);

  const checkSubscription = useCallback(async (forceRefresh: boolean = false) => {
    if (!user) {
      setSubscriptions([]);
      setChildSubscriptions([]);
      return;
    }

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = subscriptionCache.get<Subscription[]>(user.id, effectiveUserId);
      if (cached) {
        setSubscriptions(cached);
        // Cache hit
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use explicit fetch with the user's access token to guarantee the
      // Authorization header is forwarded to the edge function.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error("No active session");
      }

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-subscription`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ active_profile_id: effectiveUserId }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`check-subscription failed: ${response.status} ${text}`);
      }

      const data = await response.json();
      
      const subs = data?.subscriptions || [];
      setSubscriptions(subs);
      
      // Cache the result
      subscriptionCache.set(user.id, effectiveUserId, subs, CACHE_DURATIONS.subscriptions);
      logDataSource('production', 'Subscriptions', { count: subs.length, userId: effectiveUserId });
      
      // If acting as child, also fetch child-specific subscriptions
      if (isActingAsChild && effectiveUserId) {
        const { data: childSubs } = await supabase
          .from("child_subscriptions")
          .select("id, stripe_subscription_id, platform, tier, status, current_period_end, child_profile_id")
          .eq("child_profile_id", effectiveUserId)
          .eq("status", "active");
        
        const mappedChildSubs: Subscription[] = (childSubs || []).map((sub: any) => ({
          subscription_id: `child_${sub.id}`,
          product_id: sub.stripe_subscription_id || "",
          platform: sub.platform,
          tier: sub.tier,
          status: sub.status,
          current_period_end: sub.current_period_end || ""
        }));
        setChildSubscriptions(mappedChildSubs);
      }
    } catch (err: any) {
      // Fallback: Try querying the database directly by email
      if (user.email) {
        const directSubs = await fetchSubscriptionsDirectly(user.email);
        if (directSubs.length > 0) {
          setSubscriptions(directSubs);
          subscriptionCache.set(user.id, effectiveUserId, directSubs, CACHE_DURATIONS.subscriptions);
          setError(null);
          return;
        }
      }
      
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user, effectiveUserId, isActingAsChild, fetchSubscriptionsDirectly]);

  const createCheckout = async (productKey: ProductKey) => {
    if (!user) {
      throw new Error("Must be logged in to subscribe");
    }

    const product = STRIPE_PRODUCTS[productKey];
    if (!product || product.price_id === "custom") {
      throw new Error("This plan requires custom setup. Please contact us.");
    }

    // Navigate to on-site checkout page instead of external Stripe
    window.location.href = `/checkout?plan=${productKey}`;
  };

  const openCustomerPortal = async () => {
    if (!user) {
      throw new Error("Must be logged in to manage subscription");
    }

    const { data, error: fnError } = await supabase.functions.invoke("customer-portal");

    if (fnError) throw fnError;
    
    // Open in same tab to keep user on-site feel
    if (data?.url) {
      window.location.href = data.url;
    }
    
    return data;
  };

  // Check if user has access to a specific platform (includes child subscriptions)
  const hasSubscription = (platform: string) => {
    // Check child-specific subscriptions first when acting as child
    if (isActingAsChild) {
      const childSub = childSubscriptions.some(sub => sub.platform === platform && sub.status === "active");
      if (childSub) return true;
    }
    
    // Check direct subscriptions (parent's subscriptions are inherited)
    const directSub = subscriptions.some(sub => sub.platform === platform && sub.status === "active");
    if (directSub) return true;
    
    // Check bundle subscriptions
    const bundleSub = subscriptions.find(sub => sub.platform === "bundle" && sub.status === "active");
    if (bundleSub) {
      const bundleProduct = Object.values(STRIPE_PRODUCTS).find(
        p => p.product_id === bundleSub.product_id && 'includes' in p
      );
      if (bundleProduct && 'includes' in bundleProduct) {
        return (bundleProduct.includes as readonly string[]).includes(platform);
      }
    }
    
    return false;
  };

  // Get the specific subscription for a platform
  const getSubscription = (platform: string) => {
    return subscriptions.find(sub => sub.platform === platform);
  };

  // Get the tier for a platform
  const getTier = (platform: string): string | null => {
    const sub = subscriptions.find(sub => sub.platform === platform && sub.status === "active");
    return sub?.tier || null;
  };

  // Check if user has any coach-level access (coach or studio tiers)
  const hasCoachAccess = () => {
    return subscriptions.some(sub => 
      sub.platform === "coach" && 
      sub.status === "active"
    );
  };

  // Check if user has studio-level access (custom sales tier)
  const hasStudioAccess = () => {
    const tier = getTier("coach");
    return tier === "studio_custom" || tier === "studio_starter" || tier === "studio_pro";
  };

  // Check if user has castings access (direct or via bundle)
  const hasCastingsAccess = () => {
    return hasSubscription("castings");
  };

  // Check if user has Callback Zone access (direct or via bundle)
  const hasCallbackZoneAccess = () => {
    return hasSubscription("callback_zone");
  };

  // Check if user has AI access (not available for complimentary tier)
  const hasAIAccess = () => {
    const callbackZoneSub = subscriptions.find(
      sub => sub.platform === "callback_zone" && sub.status === "active"
    );
    
    // Complimentary tier doesn't include AI access
    if (callbackZoneSub?.tier === "complimentary") {
      return false;
    }
    
    // Check for ai_access flag from the subscription response
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subWithAIFlag = callbackZoneSub as any;
    if (subWithAIFlag?.ai_access === false) {
      return false;
    }
    
    // All other paid tiers have AI access
    return hasCallbackZoneAccess();
  };

  // Check if user has complimentary access (from box redemption)
  const hasComplimentaryAccess = () => {
    return subscriptions.some(
      sub => sub.platform === "callback_zone" && 
             sub.tier === "complimentary" && 
             sub.status === "active"
    );
  };

  // Check if user has an active free trial for a platform
  const hasFreeTrial = (platform: string) => {
    return subscriptions.some(
      sub => sub.platform === platform && 
             sub.tier === "free_trial" && 
             sub.status === "active"
    );
  };

  // Get days remaining in trial for a platform
  const getTrialDaysRemaining = (platform: string): number | null => {
    const sub = subscriptions.find(
      sub => sub.platform === platform && 
             sub.tier === "free_trial" && 
             sub.status === "active"
    );
    if (!sub?.current_period_end) return null;
    const endDate = new Date(sub.current_period_end);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-refresh every 5 minutes (300000ms) - optimized from 60s
  useEffect(() => {
    const interval = setInterval(checkSubscription, 300000);
    return () => clearInterval(interval);
  }, [checkSubscription]);

  return {
    subscriptions,
    isLoading,
    error,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    hasSubscription,
    getSubscription,
    getTier,
    hasCoachAccess,
    hasStudioAccess,
    hasCastingsAccess,
    hasCallbackZoneAccess,
    hasAIAccess,
    hasComplimentaryAccess,
    hasFreeTrial,
    getTrialDaysRemaining,
    STRIPE_PRODUCTS
  };
};
